import React, { useMemo } from "react";
import { Updater, useImmer } from "use-immer";

import {
  IgButton,
  IgTextField,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  StripeApiGetAllPricesByProductIdResponseBodyType,
  StripeApiPriceTemplateDto,
} from "@intelligems/ig-types";
import { ensureNumber, findClosestGreaterValue } from "@intelligems/ig-utils";
import { Grid, InputAdornment } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { ChoosePriceTemplateField } from "@src/features/admin/components/Fields/ChoosePriceTemplateField";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";

interface TemplateLineProps {
  productId: string;
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: React.Dispatch<CreateSubscriptionReducerAction>;
  setIsTemplate: Updater<boolean>;
}

export const TemplateLine = ({
  productId,
  payload,
  dispatchPayload,
  setIsTemplate,
}: TemplateLineProps) => {
  const [template, setTemplate] = useImmer<StripeApiPriceTemplateDto | null>(
    null
  );

  const [quantity, setQuantity] = useImmer<number>(0);

  const prices = useStripeApi<StripeApiGetAllPricesByProductIdResponseBodyType>(
    {
      action: "getPricesByProductId",
      productId: productId,
      orgs: payload.orgs,
    }
  );

  const data: StripeApiPriceTemplateDto[] | undefined = useMemo(() => {
    if (!prices.isSuccess) {
      return undefined;
    }

    const filtered = prices.data.prices.filter(
      (price) => price.active && price?.templates?.length
    );

    return filtered.flatMap((p) => p.templates);
  }, [prices.isSuccess, prices.data]);

  if (!prices.isSuccess) {
    return null;
  }

  if (!data?.some((item) => Object.keys(item.tierMap).length > 0)) {
    return (
      <Grid item textAlign={"center"}>
        <IgTypography>No templates available for this product.</IgTypography>
      </Grid>
    );
  }

  return (
    <Grid item rowSpacing={2}>
      <Grid
        container
        item
        direction={"column"}
        rowSpacing={1}
        columnSpacing={2}
      >
        <Grid item>
          <IgTooltip title="Use templates for volume-based pricing. Templates automatically select appropriate prices necessary for these plans.">
            <span>
              <IgTypography variant={"body2"}>Choose Template</IgTypography>
            </span>
          </IgTooltip>
        </Grid>
        <Grid
          container
          item
          flex={1}
          justifyContent={"space-between"}
          alignItems={"center"}
          columnSpacing={2}
          rowSpacing={2}
          flexWrap={"nowrap"}
        >
          <Grid item flex={1}>
            <ChoosePriceTemplateField
              template={template}
              setTemplate={setTemplate}
              templates={data}
              payload={payload}
              dispatchPayload={dispatchPayload}
            />
          </Grid>
          <Grid item sx={{ width: "200px" }}>
            <IgTextField
              label={"Quantity"}
              type={"number"}
              sx={{
                "& .MuiInputAdornment-root > p": {
                  fontSize: "12px",
                },
              }}
              disabled={!template}
              value={quantity || ""}
              onChange={(e) => {
                setQuantity(ensureNumber(e.target.value));
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">orders / month</InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item>
            <IgButton
              variant={"contained"}
              disabled={!quantity}
              onClick={() => {
                if (template) {
                  const priceIds = findClosestGreaterValue(
                    quantity,
                    template.tierMap
                  );
                  if (priceIds) {
                    dispatchPayload({
                      type: "setItemsByTemplate",
                      template,
                      quantity,
                      prices: prices.data.prices,
                      priceIds,
                      productId,
                    });
                    setTemplate(null);
                    setQuantity(0);
                    setIsTemplate(true);
                  }
                }
              }}
            >
              Add
            </IgButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
